import type { LoaderFunctionArgs } from '@remix-run/node'
import type { MetaFunction, NavigateFunction } from '@remix-run/react'
import { useLoaderData, useNavigate } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { cartAction } from '~/actions/cart'
import { CartList, EmptyCart, OrderPriceResume, ReceiveOrder, STEPS_URLS } from '~/design-system/components'
import ResumeSidebar from '~/design-system/components/ResumeSidebar'
import { ErrorComponent } from '~/design-system/ErrorBoundary/component'
import DefaultErrorBoundary from '~/design-system/ErrorBoundary/default'
import { Button, Toast } from '~/design-system/primitives'
import { useTrackEvents } from '~/hooks/useTrackEvents'
import { cartLoader } from '~/loaders/cart'
import type { Cart } from '~/type/Cart'

export const meta: MetaFunction = () => {
  return [
    {
      name: 'description',
      content:
        'Revise os itens selecionados para sua compra na Swift. Ajuste quantidades, verifique valores e escolha como deseja receber seus produtos.',
    },
    { title: 'Meu Carrinho - Swift' },
  ]
}

export const loader = async (context: LoaderFunctionArgs) => cartLoader({ ...context, currentStage: 'cart' })

export const action = cartAction

export const ErrorBoundary = DefaultErrorBoundary

export default function Cart() {
  const [disable, setDisable] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [showUnavailableMessage, setShowUnavailableMessage] = useState(false)

  const response = useLoaderData<typeof loader>()
  const navigate = useNavigate()
  const { trackViewtStepCheckout, trackViewCart } = useTrackEvents()

  useEffect(() => {
    trackViewtStepCheckout(response as Cart, 1)
    trackViewCart(response as Cart)
  }, [])

  useEffect(() => {
    if (response.toast) {
      setShowToast(true)
    }
  }, [response.toast])

  useEffect(() => {
    const hasCardInvalid = response?.invalidStages?.find((stage: any) => stage.url === STEPS_URLS.CART)
    setShowUnavailableMessage(hasCardInvalid?.motivation === 'hasProductUnavailable')
    const isDisabled =
      hasCardInvalid?.motivation === 'hasProductUnavailable' ||
      !response?.shippingData ||
      response?.items.some((item) => item.price === null)
    setDisable(isDisabled)
  }, [response])

  if ('error' in response || !response?.items?.length) {
    return <EmptyCart />
  }

  const handleContinue = (navigate: NavigateFunction) => () => {
    navigate('/checkout/account')
  }

  return (
    <div className="flex flex-col min-h-[480px] lg:min-h-[600px] lg:flex-row gap-5 lg:gap-4 items-start">
      <ErrorComponent>
        <CartList />
      </ErrorComponent>
      <ErrorComponent>
        <ReceiveOrder />
      </ErrorComponent>
      <ErrorComponent>
        <ResumeSidebar>
          <OrderPriceResume />
          <Button color="primary" isDisabled={disable} onPress={handleContinue(navigate)}>
            Continuar
          </Button>
        </ResumeSidebar>
      </ErrorComponent>

      {response.toast && (
        <Toast
          altText={response.toast.type}
          content={response.toast.message}
          isOpen={showToast}
          status={response.toast.type}
          onOpenChange={(isOpen) => {
            setShowToast(isOpen)
          }}
        />
      )}

      <Toast
        altText="Erro"
        content="Atenção: O carrinho foi ajustado com base na disponibilidade de produtos e tipos de entrega para o CEP inserido, o que pode ter alterado os valores e a disponibilidade dos itens."
        isOpen={showUnavailableMessage}
        status="info"
        onOpenChange={setShowUnavailableMessage}
      />
    </div>
  )
}
